import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { type VetoActionOutcome, vetoPendingActionByToken } from '../lib/api.js';

// [spec 8a1b9ddf] THE VETO THAT WORKS ON A PHONE.
//
// Top-level on purpose: /brand/$slug's beforeLoad resolves a session and
// redirects to /login without one. On 2026-10-29 the founder tapped the
// veto link from telegram, met that wall, closed the window, and the
// action fired. This route sits outside that tree and needs nothing.
//
// IT SAYS ALMOST NOTHING, and that is the design rather than laziness.
// The signed token is a bearer capability: whoever holds the url can
// use it, and a forwarded message hands it to someone else. So the page
// names no brand, no campaign and no amount. The founder already read
// the detail in the message that carried this link; repeating it here
// would disclose it to anyone else who has the url.
//
// IT NEVER ACTS ON LOAD. Mail scanners and link prefetchers fetch every
// url in a message — this is exactly why RFC 8058's one-click
// unsubscribe is a POST — so the GET renders a question and mutates
// nothing until a human taps.

export const Route = createFileRoute('/veto/$token')({
  component: VetoByTokenPage,
});

function VetoByTokenPage() {
  const { token } = useParams({ from: '/veto/$token' });
  const { t } = useLingui();
  const [outcome, setOutcome] = useState<VetoActionOutcome | null>(null);
  const [busy, setBusy] = useState(false);

  async function stopIt() {
    if (busy) return;
    setBusy(true);
    try {
      setOutcome(await vetoPendingActionByToken(token));
    } catch {
      setOutcome('failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-[460px] px-5 py-12">
      <div className="overflow-hidden rounded-2xl border-[1.5px] border-[var(--ink)] bg-white shadow-[6px_6px_0_var(--plum)]">
        <div className="px-5 pt-5">
          <h1 className="font-display text-[20px] font-bold leading-tight tracking-[-0.01em] text-[var(--ink)]">
            <TokenVetoTitle outcome={outcome} />
          </h1>
          <div className="mt-2 text-[13.5px] leading-[1.55] text-[var(--ink)]/75">
            <TokenVetoBody outcome={outcome} />
          </div>
        </div>
        <div className="mt-5 flex items-center justify-end gap-3 border-t border-[var(--line)] bg-[var(--paper)] px-5 py-3">
          {outcome === null ? (
            <button
              type="button"
              onClick={() => void stopIt()}
              disabled={busy}
              className="rounded-full bg-[var(--pulse)] px-4 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--cream)] hover:opacity-90 disabled:opacity-50"
            >
              {busy ? t`stopping…` : t`stop it`}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function TokenVetoTitle({ outcome }: { outcome: VetoActionOutcome | null }) {
  switch (outcome) {
    case null:
      return <Trans>stop the pending change?</Trans>;
    case 'ok':
      return <Trans>stopped it.</Trans>;
    case 'too_late':
      return <Trans>too late.</Trans>;
    default:
      return <Trans>that link is no good.</Trans>;
  }
}

function TokenVetoBody({ outcome }: { outcome: VetoActionOutcome | null }) {
  switch (outcome) {
    case null:
      // No brand, no campaign, no figure — see the header. The founder
      // has the detail in the message; this page may not repeat it.
      return <Trans>mag will leave everything exactly as it is.</Trans>;
    case 'ok':
      return <Trans>she won't run it.</Trans>;
    case 'too_late':
      return <Trans>that one already went through — the window closed before this got here.</Trans>;
    default:
      // One wording for every refusal: a bad signature, an expired link
      // and someone else's action must be indistinguishable from
      // outside, or the failure mode becomes an oracle.
      return <Trans>it may have expired. open magig to check.</Trans>;
  }
}
